import { AxiosError } from "axios";
import { formatISO } from "date-fns";
import { type FC, Fragment } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { toastSuccessMessage } from "../../utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "../../utils/amplitude";

import { ErrorMessageBanner } from "./ErrorMessageBanner";
import { LabeledCheckbox } from "./LabeledCheckbox";
import { SubmitOrCancelButtons } from "./SubmitOrCancelButtons";

import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { LinkButton } from "mds/components/LinkButton";
import { CircleSuccessGreenIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCourseGradePageCount, selectDesignatedCourse } from "store/selectors";

type FormValues = {
  course_grade_enabled: boolean;
  course_grade_prepwork_percentage: number;
  course_grade_activities_percentage: number;
  course_grade_assignments_percentage: number;
};

interface CourseSettingsGradeFormProps {
  className?: string;
  onClose: () => void;
}

export const CourseSettingsGradeForm: FC<CourseSettingsGradeFormProps> = ({
  className,
  onClose,
}) => {
  const currentCourse = useAppSelector(selectDesignatedCourse);

  const pageCount = useAppSelector((s) => selectCourseGradePageCount(s, currentCourse?.id));

  const form = useForm<FormValues>({
    defaultValues: {
      course_grade_enabled: currentCourse?.course_grade_enabled,
      course_grade_prepwork_percentage: currentCourse?.course_grade_prepwork_percentage,
      course_grade_activities_percentage: currentCourse?.course_grade_activities_percentage,
      course_grade_assignments_percentage: currentCourse?.course_grade_assignments_percentage,
    },
  });

  const enabled = form.watch("course_grade_enabled");

  if (!currentCourse) {
    return null;
  }

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    trackEvent("Course Settings - Save changes", BUTTON_EVENT_CATEGORY);
    return storeApi.courses
      .partial_update({ id: currentCourse.id, ...formData }, { skipToast: true })
      .then(() => {
        toastSuccessMessage(t("success.toasts.updated_course_settings"));
        onClose();
      })
      .catch((error: unknown) => {
        if (error instanceof AxiosError) {
          const validationError = error.response?.data as Record<string, string[]>;
          if (validationError && validationError.course_grade_percentages) {
            form.setError("root.course_grade_percentages", {
              type: "400",
              message: t("settings.course_grade_percentages_must_sum_to_100"),
            });
          }
        }
      });
  };

  const toggleCourseGradesPublished = () =>
    storeApi.courses.partial_update({
      id: currentCourse.id,
      course_grade_published_at: currentCourse.course_grade_published_at
        ? null
        : formatISO(new Date()),
    });

  return (
    <FormProvider {...form}>
      <form
        className={twMerge(
          "flex flex-col gap-6 text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <LabeledCheckbox
          eventName="Course Settings - Course score toggle"
          eventProps={{ courseTitle: currentCourse.title }}
          registration={form.register("course_grade_enabled")}
        >
          {t("settings.course.course_grade_enabled")}
        </LabeledCheckbox>

        <fieldset className="flex flex-col gap-2">
          <p>{t("settings.course_grade_description")}</p>
          {(["prepwork", "activities", "assignments"] as const).map((category) => (
            <Fragment key={category}>
              <label className="flex flex-row items-center justify-between">
                <div>{t(`settings.course.course_grade_${category}_percentage`)}</div>
                <div
                  className="mds-text-field-icon data-[disabled]:cursor-not-allowed data-[disabled]:bg-black-tint-90 data-[disabled]:text-black-tint-55"
                  data-disabled={!enabled || undefined}
                >
                  <input
                    className="mds-text-field mds-text-field-number text-black-tint-20 disabled:cursor-not-allowed"
                    {...form.register(`course_grade_${category}_percentage`, {
                      disabled: !enabled,
                      setValueAs: (value: string): number => {
                        if (value === "") {
                          return 0;
                        }
                        return +value;
                      },
                      validate: (value: number) => {
                        if (!Number.isInteger(value) || value < 0) {
                          return t("error.whole_number_required");
                        }
                      },
                    })}
                  />
                  <span>%</span>
                </div>
              </label>
              <ErrorMessageBanner name={`course_grade_${category}_percentage`} />
            </Fragment>
          ))}
          <ErrorMessageBanner name="root.course_grade_percentages" />
        </fieldset>

        <div className="flex flex-col gap-2 text-black-tint-20">
          {currentCourse.course_grade_published_at ? (
            <IconText
              iconStart={<CircleSuccessGreenIcon />}
              text={t("settings.course_grades_published")}
            />
          ) : (
            <div>{t("settings.course_grades_unpublished")}</div>
          )}

          <div className="text-sm font-normal">
            {currentCourse.course_grade_published_at
              ? t("settings.course_grades_description_published")
              : t("settings.course_grades_description_unpublished")}
          </div>

          <div className="text-sm font-normal">
            {t("settings.course_grades_assessment_count", { count: pageCount })}
          </div>

          <LinkButton
            className={twMerge(
              "h-[32px] disabled:text-black-tint-40",
              currentCourse.course_grade_published_at
                ? "text-red hover:text-red"
                : "text-blue hover:text-blue",
            )}
            disabled={!currentCourse.course_grade_enabled}
            eventName={
              currentCourse.course_grade_published_at
                ? "Course Settings - Unpublish Course Scores"
                : "Course Settings - Publish Course Scores"
            }
            onClick={toggleCourseGradesPublished}
          >
            {currentCourse.course_grade_published_at
              ? t("settings.unpublish_course_grades")
              : t("settings.publish_course_grades")}
          </LinkButton>
        </div>

        <SubmitOrCancelButtons className="pt-8" onCancel={onClose} />
      </form>
    </FormProvider>
  );
};
